/**
 * The words `/messages` puts on screen for what the platform sends as codes: a tip's claim state, a
 * language, and each refusal. Kept apart from the screen so the screen reads as layout.
 *
 * ★ A REFUSAL IS WORDED BY ITS CODE, AND MOST OF THE 409s HAVE NONE. `REPLY_ALREADY_EXISTS`,
 * `NOT_MESSAGE_RECIPIENT`, `MESSAGE_ACTION_NOT_PERMITTED` and `AUTHOR_DISABLED` are real members of
 * the closed vocabulary; the other conflicts - already hidden, not hidden, no answer left to take
 * down, a message its sender took back - are the uncoded 409 `CONFLICT_UNSPECIFIED`
 * (src/modules/engagement/supporter-messages-cabinet/supporter-message-acts.service.ts, "THE 409s
 * HAVE NO DOMAIN error_code EXCEPT ONE"). So a 409 is worded by WHICH ACT was refused, never by the
 * server's `detail`, and every 409 is followed by a fresh read: it means the screen was out of date.
 *
 * Design 15 (the owner's live look at real cabinets, 2026-10-01): the words are plain - "the list
 * has been refreshed", and no "platform" refusing anything; what each one says is unchanged.
 */
import { describeProblem, isApiProblem } from '../../features/cabinet/api';
import type { ErrorCode, SupporterMessage } from '../../features/cabinet/api';
import { i18n } from '../../i18n';

/** Every act on a message the screen can be refused. */
export type MessageAct = 'reply' | 'retract' | 'unhide' | 'translate';

/** What the alert says went wrong, by act. */
export function actFailed(act: MessageAct): string {
  return i18n.t(`engagement:messages.failed.${act}`);
}

/** A 409 on each act: the screen was behind the platform, and the list is read again. */
function conflict(act: MessageAct): string {
  return i18n.t(`engagement:messages.conflict.${act}`);
}

/** The refusals worded by their own code, whatever the act. */
function wordedCode(code: ErrorCode): string | undefined {
  switch (code) {
    case 'REPLY_ALREADY_EXISTS':
      return conflict('reply');
    case 'NOT_MESSAGE_RECIPIENT':
      return i18n.t('engagement:messages.refused.notRecipient');
    case 'MESSAGE_ACTION_NOT_PERMITTED':
      return i18n.t('engagement:messages.refused.notPermitted');
    default:
      return undefined;
  }
}

/** Is this a refusal that means "what you see is out of date"? The screen re-reads after one. */
export function isStaleRefusal(error: unknown): boolean {
  return isApiProblem(error) && (error.status === 409 || error.status === 404);
}

export function describeActFailure(act: MessageAct, error: unknown): string {
  if (isApiProblem(error)) {
    const worded = wordedCode(error.errorCode as ErrorCode);
    if (worded !== undefined) {
      return worded;
    }
    if (error.status === 409) {
      return conflict(act);
    }
    if (error.status === 404) {
      return i18n.t('engagement:messages.refused.gone');
    }
  }
  // AUTHOR_DISABLED, a 429 with its wait, a transport failure: the cabinet's shared words.
  return describeProblem(error);
}

type TipStatus = NonNullable<SupporterMessage['tipStatus']>;

/** A tip's claim state beside its amount (decision-log row 215, item 5): what it means for the author. */
export function tipStatusWords(status: TipStatus): string {
  return i18n.t(`engagement:messages.tipStatus.${status}`);
}

/**
 * A language code as its name in the cabinet's language, or the code itself when the runtime has no
 * name for it. `Intl.DisplayNames` is a lookup, not a clock read.
 */
export function languageName(code: string): string {
  try {
    return new Intl.DisplayNames([i18n.language], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}
